import { useTranslation } from "react-i18next";
import { DocumentReferenceResponse, GetExtraWorkAgreementQueryResponse, useGetApiConfigQuery } from "api/generatedApi";
import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "shared/toast/hooks/use-toast";
import { Box, CircularProgress, Typography } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { imageExtensions, imageMimeTypes } from "shared/documents/document-kind";
import { ThumbnailGrid } from "./thumbnailGrid";

const AcceptAttribute = imageExtensions.join(", ");

interface Props {
  agreement?: GetExtraWorkAgreementQueryResponse;
  disabled: boolean;
  setTempFilesForDelete: React.Dispatch<React.SetStateAction<DocumentReferenceResponse[]>>;
  setTempFilesforUpload: React.Dispatch<React.SetStateAction<File[]>>;
  canEdit: boolean;
  documents: DocumentReferenceResponse[];
  // Kept for backwards compatibility with parent pages; no longer used internally.
  resetLocalDocumentSwitch?: boolean;
  projectId: string;
  pendingUploads: File[];
  isUploading?: boolean;
}

export const DocumentCard = (props: Props) => {
  const { disabled, setTempFilesForDelete, setTempFilesforUpload, canEdit, documents, pendingUploads, isUploading = false } = props;
  const { t } = useTranslation();
  const { data } = useGetApiConfigQuery();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Ids of saved documents the user has marked for deletion but not yet saved to the server.
  const [pendingDeletedIds, setPendingDeletedIds] = useState<Set<string>>(new Set());

  // Drop ids that no longer exist server-side (successful save) or on explicit parent reset.
  useEffect(() => {
    setPendingDeletedIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (documents.some((doc) => doc.documentId === id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [documents]);

  useEffect(() => {
    setPendingDeletedIds(new Set());
  }, [props.resetLocalDocumentSwitch]);

  const visibleDocuments = useMemo(
    () => documents.filter((doc) => !doc.documentId || !pendingDeletedIds.has(doc.documentId)),
    [documents, pendingDeletedIds]
  );

  const canDelete = canEdit && !disabled;
  const canUpload = canEdit && !disabled;

  const nameExistsInCurrentSet = (name: string) =>
    documents.some((doc) => doc.name === name) || pendingUploads.some((file) => file.name === name);

  const makeUniqueName = (originalName: string) => {
    const extMatch = originalName.match(/(\.[^.]+)$/);
    const ext = extMatch ? extMatch[0] : "";
    const nameWithoutExt = originalName.replace(/(\.[^.]+)$/, "");
    let uniqueName = originalName;
    let counter = 0;
    while (nameExistsInCurrentSet(uniqueName)) {
      counter++;
      uniqueName = `${nameWithoutExt} (${counter})${ext}`;
    }
    return uniqueName;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const fileObj = target?.files && target.files[0];

    if (!fileObj) {
      return;
    }

    const nameLower = fileObj.name.toLowerCase();
    const hasAllowedExtension = imageExtensions.some((ext) => nameLower.endsWith(ext));
    const hasAllowedMime = fileObj.type === "" || imageMimeTypes.some((mime) => mime === fileObj.type);
    if (!hasAllowedExtension || !hasAllowedMime) {
      toast.error(t("dashboard.extraWorkAgreements.create.uploadFileTypeError"));
      target.value = "";
      return;
    }

    if (data?.maxUploadFileSizeMB && fileObj.size > data.maxUploadFileSizeMB * 1024 * 1024) {
      toast.error(t("content.overview.documents.uploadFileSizeError", { maxFileSize: data.maxUploadFileSizeMB }));
      target.value = "";
      return;
    }

    const uniqueName = makeUniqueName(fileObj.name);
    const renamedFile = uniqueName === fileObj.name ? fileObj : new File([fileObj], uniqueName, { type: fileObj.type });
    setTempFilesforUpload((prev) => [...prev, renamedFile]);

    target.value = "";
  };

  const handleDeleteSaved = (document: DocumentReferenceResponse) => {
    setTempFilesForDelete((prev) => (prev.some((d) => d.documentId === document.documentId) ? prev : [...prev, document]));
    if (document.documentId) {
      setPendingDeletedIds((prev) => {
        if (prev.has(document.documentId!)) return prev;
        const next = new Set<string>();
        prev.forEach((id) => next.add(id));
        next.add(document.documentId!);
        return next;
      });
    }
  };

  const handleDeletePending = (file: File) => {
    setTempFilesforUpload((prev) => prev.filter((f) => f.name !== file.name));
  };

  const triggerUpload = () => {
    if (isUploading || !canUpload) return;
    fileInputRef.current?.click();
  };

  const hasContent = visibleDocuments.length > 0 || pendingUploads.length > 0;

  return (
    <Box>
      {canUpload && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Box component="span" sx={{ cursor: isUploading ? "not-allowed" : "pointer", position: "relative" }} onClick={triggerUpload}>
            <FileUploadOutlinedIcon sx={{ color: "primary.main" }} />
            {isUploading && <CircularProgress size={30} sx={{ position: "absolute", top: -3, left: -3, zIndex: 1 }} />}
          </Box>
        </Box>
      )}

      {!hasContent && (
        <Typography variant="body1" color="grey.100">
          {t("dashboard.extraWorkAgreements.create.docPlaceholder")}
        </Typography>
      )}

      <ThumbnailGrid
        projectId={props.projectId}
        agreementId={props.agreement?.extraWorkAgreementId ?? ""}
        documents={visibleDocuments}
        pendingUploads={pendingUploads}
        canDelete={canDelete}
        onDeleteSaved={handleDeleteSaved}
        onDeletePending={handleDeletePending}
      />

      <input
        style={{ display: "none" }}
        ref={fileInputRef}
        type="file"
        accept={AcceptAttribute}
        onChange={handleFileChange}
      />
    </Box>
  );
};
